import { Link } from "react-router-dom";
import StoreHeader from "../../components/layout/StoreHeader";
import StoreFooter from "../../components/layout/StoreFooter";
import { useSettings } from "../../context/SettingsContext";
import { ShieldCheck, Truck, Clock, Users, MapPin, Phone } from "lucide-react";

const VALUES = [
  { icon: ShieldCheck, title: "Quality You Can Trust",    body: "Every part is inspected before it leaves our yard. Most used parts carry a 30–90 day warranty. If something is wrong, we make it right." },
  { icon: Truck,       title: "Australia-Wide Delivery",  body: "Same-day dispatch before 2pm. Free shipping over $250. We ship to every state and territory, metro and regional." },
  { icon: Clock,       title: "15+ Years Experience",     body: "Brisbane Car Parts has been supplying Brisbane and Queensland with quality used parts since 2009. We know cars, we know parts." },
  { icon: Users,       title: "Real People, Real Support",body: "Call us Monday to Saturday and speak to a real parts expert, not a chatbot. We will help you find exactly what you need." },
];

const STATS = [
  { n: "15+",  label: "Years in Business" },
  { n: "10K+", label: "Parts in Stock"    },
  { n: "90",   label: "Day Warranty"      },
  { n: "4.8",  label: "Google Rating"     },
];

export default function About() {
  const { settings } = useSettings();
  const phone = settings.store_phone   || "07 3875 1024";
  const hours = settings.business_hours || "Mon–Fri 8:00am–5:30pm, Sat 9:00am–1:00pm";

  return (
    <div className="bg-white min-h-screen">
      <StoreHeader />

      {/* Page header */}
      <div className="bg-[#0b1a3b] py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">Who we are</div>
          <h1 className="font-heading text-[44px] font-black uppercase text-white leading-tight">
            About Brisbane Car Parts
          </h1>
          <p className="text-blue-200 text-[15px] mt-4 max-w-lg leading-relaxed">
            Brisbane's trusted supplier of quality used car parts since 2009.
          </p>
        </div>
      </div>

      {/* Our story */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
        <div>
          <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-3">Our story</div>
          <h2 className="font-heading text-[34px] font-black uppercase text-[#0b1a3b] mb-5 leading-tight">
            Local Wreckers, Brisbane Built
          </h2>
          <div className="space-y-4 text-[15px] text-zinc-600 leading-relaxed">
            <p>
              Brisbane Car Parts (BCP) started with a simple idea: Queenslanders deserve quality used
              parts at fair prices, backed by real local support. We opened our Richlands yard over
              15 years ago and have been growing ever since.
            </p>
            <p>
              Every part we sell is pulled from real vehicles, inspected by our team, and backed by
              our warranty — so when you buy from us, you know exactly what you're getting. It's the
              same standard of care we'd want if we were the ones buying the part.
            </p>
            <p>
              We dismantle vehicles every week, which means fresh stock is always coming through the
              yard. Whatever the job — a weekend repair, a full workshop job, or just getting your
              daily driver back on the road — our team can help you find the right part, at the
              right price.
            </p>
            <p>
              And if we don't have it in stock, we'll track it down through our network of trusted
              wreckers across Australia.
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/shop"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#e31e24] hover:bg-[#b71820] text-white font-heading font-black uppercase tracking-wide text-[14px] transition-colors">
              Shop Parts
            </Link>
            <Link to="/quote"
              className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#0b1a3b] hover:bg-[#0b1a3b] hover:text-white text-[#0b1a3b] font-heading font-black uppercase tracking-wide text-[14px] transition-colors">
              Request a Quote
            </Link>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 overflow-hidden rounded-sm" style={{ aspectRatio: "16/7" }}>
            <img src="https://images.unsplash.com/photo-1645216755684-c946ce202427?w=800&q=80" alt="Rows of vehicles at our Brisbane wrecking yard" className="w-full h-full object-cover" />
          </div>
          <div className="overflow-hidden rounded-sm" style={{ aspectRatio: "1" }}>
            <img src="https://images.unsplash.com/photo-1627927394579-63688a0d8301?w=400&q=80" alt="Used wheel and tyre ready for resale" className="w-full h-full object-cover" />
          </div>
          <div className="overflow-hidden rounded-sm" style={{ aspectRatio: "1" }}>
            <img src="https://images.unsplash.com/photo-1668445046413-59f8ea2e0b4d?w=400&q=80" alt="Engine and mechanical parts pulled for resale" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-[#0b1a3b] py-14 mb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {STATS.map(({ n, label }) => (
            <div key={label}>
              <div className="font-heading text-[48px] font-black text-[#e31e24] leading-none">{n}</div>
              <div className="font-heading font-bold uppercase text-white text-[14px] mt-2 tracking-wide">{label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 mb-16">
        <div className="text-center mb-10">
          <div className="text-[#e31e24] font-mono text-[11px] uppercase tracking-[0.3em] mb-2">What we stand for</div>
          <h2 className="font-heading text-[34px] font-black uppercase text-[#0b1a3b]">Our Values</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {VALUES.map(({ icon: Icon, title, body }) => (
            <div key={title} className="border border-zinc-200 rounded-sm p-6 hover:border-[#1e5bb8] hover:shadow-md transition-all">
              <div className="w-11 h-11 bg-[#e31e24]/10 border border-[#e31e24]/20 rounded-sm mb-4 flex items-center justify-center">
                <Icon className="h-5 w-5 text-[#e31e24]" />
              </div>
              <h3 className="font-heading font-black text-[15px] uppercase text-[#0b1a3b] mb-2">{title}</h3>
              <p className="text-[13px] text-zinc-600 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Contact info strip */}
      <section className="bg-zinc-50 border-y border-zinc-200 py-14 mb-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 bg-[#e31e24]/10 border border-[#e31e24]/20 rounded-sm shrink-0 flex items-center justify-center">
              <MapPin className="h-5 w-5 text-[#e31e24]" />
            </div>
            <div>
              <div className="font-heading font-black uppercase text-[#0b1a3b] text-[15px] mb-1">Location</div>
              <div className="text-[14px] text-zinc-600">83 Teraba Street</div>
              <div className="text-[14px] text-zinc-600">Richlands QLD 4077</div>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 bg-[#e31e24]/10 border border-[#e31e24]/20 rounded-sm shrink-0 flex items-center justify-center">
              <Phone className="h-5 w-5 text-[#e31e24]" />
            </div>
            <div>
              <div className="font-heading font-black uppercase text-[#0b1a3b] text-[15px] mb-1">Phone</div>
              <a href={`tel:${phone.replace(/\s/g,"")}`}
                className="text-[14px] text-[#1e5bb8] font-medium hover:text-[#0b3ea0] transition-colors">
                {phone}
              </a>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 bg-[#e31e24]/10 border border-[#e31e24]/20 rounded-sm shrink-0 flex items-center justify-center">
              <Clock className="h-5 w-5 text-[#e31e24]" />
            </div>
            <div>
              <div className="font-heading font-black uppercase text-[#0b1a3b] text-[15px] mb-1">Hours</div>
              <div className="text-[14px] text-zinc-600">{hours}</div>
            </div>
          </div>
        </div>
      </section>

      <StoreFooter />
    </div>
  );
}